import {supabase} from './supabaseClient';
import type {RootState} from '../store/store';
import {debugLog, debugWarn} from './debug';

// Store reference to get state
let getState: (() => RootState) | null = null;

/**
 * Initialize the auth module with the Redux store
 * Call this once during app initialization
 */
export function initializeAuthStore(storeGetState: () => RootState) {
  getState = storeGetState;
}

/**
 * Gets the current JWT token from the Redux store first, falling back to Supabase.
 *
 * The Redux read is kept because it is synchronous and avoids a round trip through Supabase's
 * storage layer on every API call.
 */
export async function getJWTToken(): Promise<string | null> {
  try {
    // Try to get from Redux first (fastest and most reliable)
    if (getState) {
      const session = getState().auth.session;
      if (session?.access_token) {
        debugLog('[getJWTToken] Got token from Redux store');
        return session.access_token;
      }
      debugLog('[getJWTToken] No session in Redux store');
    } else {
      debugWarn('[getJWTToken] Redux store not initialized');
    }

    // Fallback to Supabase getSession. This also transparently refreshes an expired token.
    debugLog('[getJWTToken] Falling back to Supabase getSession');
    const {data: {session}, error} = await supabase.auth.getSession();

    if (error) {
      console.error('[getJWTToken] Supabase getSession error:', error);
      return null;
    }

    if (session?.access_token) {
      debugLog('[getJWTToken] Got token from Supabase');
      return session.access_token;
    }

    debugWarn('[getJWTToken] No token available from any source');
    return null;
  } catch (error) {
    console.error('[getJWTToken] Error getting token:', error);
    return null;
  }
}
